// The plain values and checks of a provisioning recipe (F-CAT-09, ADR-0045), apart from the route file so the HQ console can
// check a recipe as it is typed — through `@clavian/contracts/constants`, with no Zod and no `new Function` in its bundle —
// against the same rules the server's schema and the station hold. `routes/hq-catalogue.ts` builds `AppProvisioningSchema`
// from these.
/**
 * Process names a recipe may never ask a station to close: the station's own processes, the kiosk's WebView2, Windows'
 * shell and session processes and its core services. Lower case, no `.exe` — as process names are stored. The station holds
 * the same list (`packages/contracts/src/__tests__/protected-process-names.test.ts` keeps the two in step) and refuses them
 * too.
 */
export const PROTECTED_PROCESS_NAMES = [
    'clavian.station.agent',
    'clavian.station.service',
    'clavian.station.cli',
    'msedgewebview2',
    'explorer',
    'dwm',
    'csrss',
    'winlogon',
    'wininit',
    'lsass',
    'lsaiso',
    'services',
    'smss',
    'svchost',
    'system',
    'registry',
    'memory compression',
    'fontdrvhost',
    'sihost',
    'ctfmon',
    'taskhostw',
    'runtimebroker',
    'searchhost',
    'searchindexer',
    'startmenuexperiencehost',
    'shellexperiencehost',
    'textinputhost',
    'userinit',
    'logonui',
    'conhost',
    'audiodg',
    'spoolsv',
    'dllhost',
    'wudfhost',
    'securityhealthservice',
    'msmpeng',
];
/**
 * The always-blocked Windows tools (spec/11 §3.4) a recipe may name as ones its installed app is known to start
 * (`expectedBlockedChildren`, F-CAT-09, ADR-0045 §9). They stay blocked; a block whose parent is the verified app is recorded
 * quietly instead of raising a tamper alert. Lower case, no `.exe`, as process names are stored. `reg`: Discord's
 * `Discord.exe` runs `reg.exe` at every start to register its `discord://` link and read its Run value. The station holds
 * the same list (`ProvisioningChildTools`) and refuses anything else. Shells and script hosts are never on it.
 */
export const PROVISIONING_CHILD_TOOLS = ['reg'];
/**
 * Install roots too broad to snapshot: the first folder under `{LocalAppData}`/`{AppData}` that holds many apps' or Windows'
 * own files. An app's root is its own folder (`{LocalAppData}\Discord`), or one inside these (`{LocalAppData}\Programs\X`).
 */
export const BROAD_INSTALL_ROOTS = ['packages', 'microsoft', 'temp', 'programs', 'low', 'clavian', 'crashdumps', 'd3dscache'];
/** A per-user file: `{LocalAppData}`, `{AppData}` or `{UserProfile}` first, backslashes, no `.`/`..` segment, no `%`. */
export const PER_USER_PATH_PATTERN = /^\{(?:LocalAppData|AppData|UserProfile)\}\\(?!.*(?:^|\\)\.\.?(?:\\|$))[^%:*?"<>|/]+$/i;
/** An app's install root: its own folder under `{LocalAppData}` or `{AppData}`, at least one folder deep. */
export const INSTALL_ROOT_PATTERN = /^\{(?:LocalAppData|AppData)\}(?:\\(?!\.\.?(?:\\|$))[^%{}:*?"<>|/\\]+)+$/i;
/** A path relative to an install root that holds data: backslashes, no drive, no leading slash, no `..`, no variables. */
export const RESTORE_EXCLUDE_PATTERN = /^(?![\\/])(?!.*(?:^|\\)\.\.?(?:\\|$))[^%{}:*?"<>|/]+$/;
/** A process name as it is stored and compared: trimmed, lower case, one trailing `.exe` removed. */
function comparable(name) {
    const lower = name.trim().toLowerCase();
    return lower.endsWith('.exe') ? lower.slice(0, -4) : lower;
}
/** Whether a recipe naming `name` among the processes to close names one of the PC's own (never closed). */
export function isProtectedProcessName(name) {
    return PROTECTED_PROCESS_NAMES.includes(comparable(name));
}
/** Whether `root` (a valid install root) is one of the broad folders that hold other apps' files. */
export function isBroadInstallRoot(root) {
    const parts = root.trim().replace(/\\+$/, '').split('\\');
    return parts.length === 2 && BROAD_INSTALL_ROOTS.includes((parts[1] ?? '').toLowerCase());
}
/** A per-user path, lower case, with `{UserProfile}\AppData\Local` and `…\Roaming` spelled as the variables they are. */
function canonicalPerUserPath(path) {
    return path
        .trim()
        .toLowerCase()
        .replace(/^\{userprofile\}\\appdata\\local(?=\\|$)/, '{localappdata}')
        .replace(/^\{userprofile\}\\appdata\\roaming(?=\\|$)/, '{appdata}')
        .replace(/\\+$/, '');
}
/** Whether the per-user `path` is the install `root` or inside it (case-insensitive, the profile's AppData as variables). */
export function isInsideInstallRoot(path, root) {
    const p = canonicalPerUserPath(path);
    const r = canonicalPerUserPath(root);
    return p === r || p.startsWith(`${r}\\`);
}
/**
 * The host of an `https:` URL, lower case, or undefined when it is not one — no user info, an optional `:443` only. A regex,
 * not `URL`: the contract runs where no DOM or Node types are declared.
 */
export function httpsHostOf(url) {
    const match = /^https:\/\/([A-Za-z0-9.-]+)(?::443)?(?:[/?#][^\s]*)?$/i.exec(url.trim());
    const host = match?.[1]?.toLowerCase();
    return host === undefined || host.startsWith('.') || host.endsWith('.') || host.includes('..') ? undefined : host;
}
//# sourceMappingURL=app-provisioning.js.map